import { Box, useMediaQuery } from "@mui/material";
import {
    Chart as ChartJS,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { useRef, useEffect } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useTheme } from "@emotion/react";

ChartJS.register(ArcElement, Title, Tooltip, Legend, ChartDataLabels);

// These indicators carry a single yes/no reading, which a pie chart cannot express. The answer
// is drawn as a pill - soft background, darker text of the same hue - straight onto the canvas:
// the arc below is kept, and made invisible, only because it keeps this a real Chart.js chart,
// which the PNG export needs.
const PILL_STYLES = {
    yes: { background: "#c8e6c9", text: "#1b5e20" },
    no: { background: "#ffd6d6", text: "#b3261e" },
};

// CanvasRenderingContext2D.roundRect is missing from older Safari and Firefox, so the shape is
// traced by hand.
const tracePill = (ctx, x, y, width, height) => {
    const radius = height / 2;
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.arc(x + width - radius, y + radius, radius, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(x + radius, y + height);
    ctx.arc(x + radius, y + radius, radius, Math.PI / 2, (3 * Math.PI) / 2);
    ctx.closePath();
};

const yesNoPill = {
    id: "yesNoPill",
    afterDatasetsDraw(chart) {
        const opts = chart.options.plugins?.yesNoPill;
        if (!opts) return;

        const { ctx, chartArea } = chart;
        if (!chartArea) return;

        const midX = (chartArea.left + chartArea.right) / 2;
        const midY = (chartArea.top + chartArea.bottom) / 2;
        const side = Math.min(chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);

        // A badge, not a poster: it grows with the card but stays within readable bounds.
        const height = Math.min(64, Math.max(36, side * 0.18));
        const width = height * 2.4;
        const style = opts.isTrue ? PILL_STYLES.yes : PILL_STYLES.no;

        ctx.save();
        tracePill(ctx, midX - width / 2, midY - height / 2, width, height);
        ctx.fillStyle = style.background;
        ctx.fill();

        ctx.fillStyle = style.text;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = `500 ${Math.round(height * 0.44)}px ${opts.fontFamily}`;
        ctx.fillText(opts.label, midX, midY + 1);
        ctx.restore();
    },
};

export default function StatusChart({ data, showDataLabels, hideSubregions, title }) {
    const theme = useTheme();
    const ref = useRef(null);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        ChartJS.defaults.font.size = isMobile ? 9 : 11;
    }, [isMobile]);

    const dataset = data.datasets?.[0] || { data: [], backgroundColor: [] };
    const value = dataset.data[0] || 0;
    const isTrue = value === 1 || value === true || String(value).trim().toLowerCase() === "yes";

    const label = isTrue ? "Yes" : "No";

    const chartData = {
        labels: [label],
        datasets: [
            {
                // Invisible on purpose: the symbol drawn by the plugin is the visual, the arc
                // only keeps a Chart.js instance alive, which the PNG export needs.
                data: [1],
                backgroundColor: ["transparent"],
                borderWidth: 0,
            },
        ],
    };

    const options = {
        layout: {
            padding: { top: 35, right: isMobile ? 8 : 12, bottom: 12, left: isMobile ? 5 : 10 },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            // The pill already says Yes or No: a legend repeating the same word underneath is
            // noise.
            legend: { display: false },
            yesNoPill: {
                isTrue,
                label,
                fontFamily: theme.typography?.fontFamily || "sans-serif",
            },
            // Off here: the pill carries the word itself.
            datalabels: { display: false },
            // Nothing to add: the pill already carries the whole answer.
            tooltip: { enabled: false },
            title: {
                display: false, // will be toggled to true for PNG export
                text: title,
                font: { size: 14, lineHeight: 1.2 },
                padding: { top: 20, left: 0, right: 0, bottom: 10 },
            },
        },
    };

    return (
        <Box className="max-chart-height">
            <Pie ref={ref} options={options} data={chartData} plugins={[yesNoPill]} />
        </Box>
    );
}
